import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Sparkles, Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="h-12 w-12 rounded-2xl gradient-orange grid place-items-center shadow-md">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div className="text-left">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Happy Journey 4U</div>
            <div className="text-xl font-bold">HJ4U Campaigns</div>
          </div>
        </div>

        <div className="glass-card p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1.5">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Sign in to manage WhatsApp campaigns, contacts, and templates.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="pl-9 bg-white/60 border-white/60 rounded-xl h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  className="pl-9 bg-white/60 border-white/60 rounded-xl h-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 gradient-orange text-white hover:opacity-95 rounded-xl shadow-md"
              disabled={submitting || loading}
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Sign in
            </Button>
          </form>
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground">
          Single business user • Glass theme · Powered by Meta WhatsApp Cloud API & Gemini AI
        </p>
      </div>
    </div>
  );
}
